import recallsFeedServices from '../../../../services/notisphere/recalls-feed'
import purchaseHistoryServices from '../../../../services/provider/purchase-history'
import { ENUM_RECALL_FEED_DECISION, ENUM_RECALL_FEED_IMPACT } from '../../../../misc/commons'

const FIXED_FIELDS = [
    { key: 'purchase_date', label: 'Purchase Date' },
    { key: 'file_name', label: 'Purchase History Filename' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'product_description', label: 'Hospital Description' },
    { key: 'catalog_number', label: 'Catalog #' },
    { key: 'manufacturer_item_number', label: 'Manufacturer #' },
    { key: 'po_number', label: 'PO #' },
    { key: 'quantity', label: 'Qty' },
    { key: 'location', label: 'Location' },
    { key: 'match_tier', label: 'Match' }
]

const IMPACT_BADGE_STYLES = {
    [ENUM_RECALL_FEED_IMPACT.DEFINITE]: { text: 'Definite', color: '#fff', backgroundColor: '#F62D51' },
    [ENUM_RECALL_FEED_IMPACT.LIKELY]: { text: 'Likely', color: '#fff', backgroundColor: '#F56827' },
    [ENUM_RECALL_FEED_IMPACT.POSSIBLE]: { text: 'Possible', color: '#000', backgroundColor: '#ffc107' },
    [ENUM_RECALL_FEED_IMPACT.NONE]: { text: 'None', color: '#fff', backgroundColor: '#6c757d' }
}

export default {
    props: {
        recallData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            errorMessage: null,
            purchaseHistory: null,
            decisionHistory: [],
            decision: null,
            comments: '',
            initialDecision: null,
            initialComments: '',
            isSaving: false,
            forceClose: false,
            commentMaxLength: 250,
            decisionOptions: [
                { value: ENUM_RECALL_FEED_DECISION.CREATE_RECALL, text: 'Create Recall' },
                { value: ENUM_RECALL_FEED_DECISION.NO_PURCHASE_HISTORY, text: 'No Purchase History' },
                { value: ENUM_RECALL_FEED_DECISION.NEEDS_REVIEW, text: 'Needs Review' }
            ]
        }
    },
    computed: {
        analysisStatus() {
            return this.purchaseHistory ? this.purchaseHistory.analysis_status : null
        },
        isRunning() {
            return this.analysisStatus === 'RUNNING'
        },
        isFailed() {
            return this.analysisStatus === 'FAILED'
        },
        isNoData() {
            return this.analysisStatus === 'NO_DATA'
        },
        isCompleted() {
            return this.analysisStatus === 'COMPLETED'
        },
        rows() {
            return (this.purchaseHistory && this.purchaseHistory.rows) || []
        },
        impact() {
            return this.purchaseHistory ? this.purchaseHistory.impact : null
        },
        impactBadge() {
            const badge = IMPACT_BADGE_STYLES[this.impact]
            return badge ? { ...badge, text: this.impact === ENUM_RECALL_FEED_IMPACT.NONE ? badge.text : `${badge.text} Match` } : null
        },
        confirmedLocations() {
            return this.uniqueLocations(this.rows.filter(r => r.match_tier === ENUM_RECALL_FEED_IMPACT.DEFINITE))
        },
        needsReviewLocations() {
            return this.uniqueLocations(this.rows.filter(r => r.match_tier === ENUM_RECALL_FEED_IMPACT.LIKELY || r.match_tier === ENUM_RECALL_FEED_IMPACT.POSSIBLE))
        },
        gridFields() {
            const dynamicFields = ((this.purchaseHistory && this.purchaseHistory.columns) || [])
                .map(c => ({ key: c.field, label: c.header_name || c.field }))
            return [...FIXED_FIELDS, ...dynamicFields]
        },
        displayRows() {
            const columns = (this.purchaseHistory && this.purchaseHistory.columns) || []
            const sharedKeys = FIXED_FIELDS.map(f => f.key).filter(k => k !== 'file_name')
            const groups = new Map()

            this.rows.forEach(row => {
                const key = JSON.stringify([
                    ...sharedKeys.map(k => row[k] ?? null),
                    ...columns.map(c => (row.provider_fields ? row.provider_fields[c.field] : null) ?? null)
                ])
                if (!groups.has(key)) groups.set(key, [])
                groups.get(key).push(row)
            })

            return [...groups.values()].map(group => {
                const first = group[0]
                const flat = { ...first }

                columns.forEach(c => {
                    const value = first.provider_fields ? first.provider_fields[c.field] : undefined
                    flat[c.field] = this.orDash(value)
                })
                sharedKeys.forEach(k => {
                    if (k === 'location') {
                        flat[k] = first.location || 'Not Available'
                    } else if (k !== 'match_tier') {
                        flat[k] = this.orDash(first[k])
                    }
                })

                // 2.13: one row per product, with every contributing source listed newest-file-first.
                flat.source_entries = [...group]
                    .sort((a, b) => this.sourceSortValue(b) - this.sourceSortValue(a))
                    .map(r => ({ label: this.formatSourceEntry(r), fileId: r.purchase_order_file_id }))

                return flat
            })
        },
        isDirty() {
            return this.decision !== this.initialDecision || this.comments !== this.initialComments
        },
        commentsRemaining() {
            return this.commentMaxLength - (this.comments ? this.comments.length : 0)
        },
        changeLogFields() {
            return [
                { key: 'created_datetime', label: 'Date/Time' },
                { key: 'created_by', label: 'User' },
                { key: 'decision', label: 'Change' },
                { key: 'comments', label: 'Comments' }
            ]
        }
    },
    methods: {
        decisionLabel(value) {
            const found = this.decisionOptions.find(o => o.value === value)
            return found ? found.text : value
        },
        show() {
            this.resetState()
            this.$refs.modal.show()
            this.loadData()
        },
        resetState() {
            this.loading = false
            this.errorMessage = null
            this.purchaseHistory = null
            this.decisionHistory = []
            this.decision = null
            this.comments = ''
            this.initialDecision = null
            this.initialComments = ''
            this.isSaving = false
            this.forceClose = false
        },
        loadData() {
            if (!this.recallData || !this.recallData.id) return
            this.loading = true
            Promise.all([
                recallsFeedServices.getRecallPurchaseHistory(this.recallData.id),
                recallsFeedServices.getRecallFeedDecisionHistory(this.recallData.id)
            ]).then(([purchaseHistoryResp, historyResp]) => {
                if (purchaseHistoryResp && !purchaseHistoryResp.error) {
                    this.purchaseHistory = purchaseHistoryResp.data.d.data
                } else {
                    this.errorMessage = 'Purchase history analysis could not be completed.'
                }
                if (historyResp && !historyResp.error) {
                    this.decisionHistory = historyResp.data.d.data || []
                }
            }).catch(() => {
                this.errorMessage = 'Purchase history analysis could not be completed.'
            }).finally(() => {
                this.loading = false
            })
        },
        uniqueLocations(rows) {
            const locations = rows
                .map(r => r.location)
                .filter(l => l && l !== '')
            return [...new Set(locations)]
        },
        orDash(value) {
            return (value === null || value === undefined || value === '') ? '—' : value
        },
        sourceSortValue(row) {
            const raw = row.file_uploaded_datetime || row.purchase_date
            const parsed = raw ? new Date(raw).getTime() : NaN
            return isNaN(parsed) ? 0 : parsed
        },
        // "[Filename] [Month] [YYYY] - Line No [Number]"
        formatSourceEntry(row) {
            const parts = [row.file_name || 'Unknown file']
            const raw = row.file_uploaded_datetime || row.purchase_date
            const date = raw ? new Date(raw) : null
            if (date && !isNaN(date.getTime())) {
                parts.push(date.toLocaleString('en-US', { month: 'long' }), date.getFullYear())
            }
            const lineNo = row.source_row_number
            return `${parts.join(' ')}${lineNo ? ` - Line No ${lineNo}` : ''}`
        },
        formatLocationsSummary(locations) {
            if (!locations || locations.length === 0) return 'Not Available'
            if (locations.length <= 2) return locations.join(', ')
            return `${locations.slice(0, 2).join(', ')} +${locations.length - 2} more`
        },
        matchBadgeVariant(tier) {
            return 'secondary'
        },
        matchBadgeText(tier) {
            return IMPACT_BADGE_STYLES[tier]?.text || '—'
        },
        matchBadgeStyle(tier) {
            const badge = IMPACT_BADGE_STYLES[tier]
            return badge ? { color: badge.color, backgroundColor: badge.backgroundColor } : null
        },
        openSourceFile(fileId) {
            if (!fileId) return
            purchaseHistoryServices.downloadPOImportFile(fileId).then(resp => {
                const url = resp && resp.data && resp.data.d && resp.data.d.data
                if (url) {
                    window.open(url, '_blank', 'noopener')
                } else {
                    this._showNotify('File unavailable', 'The purchase history source file could not be opened.', 'danger', { hideFooter: false })
                }
            }).catch(() => {
                this._showNotify('File unavailable', 'The purchase history source file could not be opened.', 'danger', { hideFooter: false })
            })
        },
        formatDateTime(date) {
            if (!date) return '-'
            const d = new Date(date)
            if (isNaN(d.getTime())) return '-'
            return d.toLocaleString('en-US', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: true
            })
        },
        onCancel() {
            if (this.isDirty) {
                this._showConfirm('You have unsaved changes. Are you sure you want to discard them?').then(ok => {
                    if (ok) {
                        this.forceClose = true
                        this.$refs.modal.hide()
                    }
                })
            } else {
                this.$refs.modal.hide()
            }
        },
        onHide(bvModalEvent) {
            if (this.forceClose || bvModalEvent.trigger === 'save') {
                this.forceClose = false
                return
            }
            if (this.isDirty) {
                bvModalEvent.preventDefault()
                this._showConfirm('You have unsaved changes. Are you sure you want to discard them?').then(ok => {
                    if (ok) {
                        this.forceClose = true
                        this.$refs.modal.hide()
                    }
                })
            }
        },
        save() {
            if (!this.decision) {
                this._showNotify('Decision required', 'Please select a decision before saving.', 'warning', { hideFooter: false })
                return
            }
            this.isSaving = true
            const payload = {
                decision: this.decision,
                comments: this.comments,
                impact: this.impact,
                analysisVersion: this.purchaseHistory && this.purchaseHistory.analysis_version ? JSON.stringify(this.purchaseHistory.analysis_version) : null
            }
            recallsFeedServices.saveRecallFeedDecision(this.recallData.id, payload).then(resp => {
                if (resp && !resp.error) {
                    this._showToast('Decision saved successfully.')
                    this.$emit('onCompleted', { decision: this.decision, recallData: this.recallData })
                    this.$refs.modal.hide('save')
                } else {
                    this._showNotify('Save failed', (resp && resp.msg) || 'Unable to save your decision. Please try again.', 'danger', { hideFooter: false })
                }
            }).catch(() => {
                this._showNotify('Save failed', 'Unable to save your decision. Please try again.', 'danger', { hideFooter: false })
            }).finally(() => {
                this.isSaving = false
            })
        }
    }
}
